export const permissionSetFromSession = (session) => {
  const values = session?.permissions;

  if (!Array.isArray(values)) {
    return new Set();
  }

  return new Set(
    values.filter(
      (permission) =>
        typeof permission === "string"
        && permission.length > 0
    )
  );
};

const asPermissionSet = (permissionsOrSession) => {
  if (permissionsOrSession instanceof Set) {
    return permissionsOrSession;
  }

  return permissionSetFromSession(permissionsOrSession);
};

export const hasPermission = (
  permissionsOrSession,
  permission
) => {
  const permissions = asPermissionSet(permissionsOrSession);

  return permissions.has("ALL") || permissions.has(permission);
};

export const hasAllPermissions = (
  permissionsOrSession,
  requiredPermissions
) => {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  const permissions = asPermissionSet(permissionsOrSession);

  return requiredPermissions.every(
    (permission) => hasPermission(permissions, permission)
  );
};

export const hasAnyPermission = (
  permissionsOrSession,
  requiredPermissions
) => {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  const permissions = asPermissionSet(permissionsOrSession);

  return requiredPermissions.some(
    (permission) => hasPermission(permissions, permission)
  );
};

export const sessionFromGui = (gui) => gui?.props?.session || null;

export const can = (gui, permission) =>
  hasPermission(sessionFromGui(gui), permission);

export const canAll = (gui, requiredPermissions) =>
  hasAllPermissions(sessionFromGui(gui), requiredPermissions);

export const canAny = (gui, requiredPermissions) =>
  hasAnyPermission(sessionFromGui(gui), requiredPermissions);

const descriptorAllowed = (descriptor, permissions) => {
  if (!descriptor) {
    return false;
  }

  return hasAllPermissions(permissions, descriptor.requires)
    && hasAnyPermission(permissions, descriptor.requiresAny);
};

const stripPermissionMetadata = (descriptor) => {
  const {
    requires,
    requiresAny,
    options,
    ...visibleDescriptor
  } = descriptor;

  return visibleDescriptor;
};

const filterOptions = (options, permissions) => {
  const visibleOptions = {};

  for (const [key, option] of Object.entries(options || {})) {
    if (!descriptorAllowed(option, permissions)) {
      continue;
    }

    visibleOptions[key] = stripPermissionMetadata(option);
  }

  return visibleOptions;
};

const filterSection = (section, permissions) => {
  if (!descriptorAllowed(section, permissions)) {
    return null;
  }

  const hasOptions = section.options
    && Object.keys(section.options).length > 0;

  if (!hasOptions) {
    return stripPermissionMetadata(section);
  }

  const visibleOptions = filterOptions(section.options, permissions);
  const firstVisibleOption = Object.values(visibleOptions)[0];

  if (!firstVisibleOption) {
    return null;
  }

  return {
    ...stripPermissionMetadata(section),
    path: firstVisibleOption.path || section.path,
    options: visibleOptions
  };
};

export const filterMenuByPermissions = (menuDefinition, session) => {
  const permissions = permissionSetFromSession(session);
  const visibleMenu = {};

  for (const [key, section] of Object.entries(menuDefinition || {})) {
    const visibleSection = filterSection(section, permissions);

    if (visibleSection) {
      visibleMenu[key] = visibleSection;
    }
  }

  return visibleMenu;
};


const GEOGRAPHY_PERMISSION_NAMES = Object.freeze({
  department: "DEPARTMENT",
  arrondissement: "ARRONDISSEMENT",
  commune: "COMMUNE",
  section: "SECTION",
  constituency: "CONSTITUENCY",
  "voting-center": "VOTING_CENTER"
});

const geographyPermission = (operation, entity) => {
  const suffix = GEOGRAPHY_PERMISSION_NAMES[entity];

  if (!suffix) {
    throw new Error(`Unknown geography entity: ${entity}`);
  }

  return `${operation}_${suffix}`;
};

export const canCreateGeographyEntity = (gui, entity) =>
  can(gui, geographyPermission("CREATE", entity));

export const canUpdateGeographyEntity = (gui, entity) =>
  can(gui, geographyPermission("UPDATE", entity));

export const canOpenGeographyContext = (gui, entity) => {
  if (canUpdateGeographyEntity(gui, entity)) {
    return true;
  }

  switch (entity) {
    case "department":
      return canCreateGeographyEntity(gui, "arrondissement");
    case "arrondissement":
    case "constituency":
      return canCreateGeographyEntity(gui, "commune");
    case "commune":
      return canCreateGeographyEntity(gui, "section");
    case "section":
      return canCreateGeographyEntity(gui, "voting-center");
    default:
      return false;
  }
};
